import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Brain, Mail, TrendingUp, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAssistant = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [tone, setTone] = useState('professional');
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [paymentAnalysis, setPaymentAnalysis] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchInvoices();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data.filter(inv => inv.status !== 'paid'));
    } catch (error) {
      console.error('Failed to fetch invoices');
    }
  };

  const handleGenerateEmail = async () => {
    if (!selectedInvoice) {
      toast.error('Please select an invoice');
      return;
    }

    setIsGenerating(true);
    try {
      const invoice = invoices.find(inv => inv.id === selectedInvoice);
      const client = clients.find(c => c.id === invoice.client_id);
      const dueDate = new Date(invoice.due_date);
      const daysOverdue = Math.max(0, Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24)));

      const response = await axios.post(`${API}/ai/generate-email`, {
        client_name: client.name,
        invoice_number: invoice.invoice_number,
        amount: invoice.amount,
        due_date: invoice.due_date,
        days_overdue: daysOverdue,
        tone: tone,
      });

      setGeneratedEmail(response.data.email);
      toast.success('Email generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate email');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnalyzePatterns = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ai/analyze-payment-patterns`, {
        client_id: selectedClient,
      });

      setPaymentAnalysis(response.data);
      toast.success('Analysis complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze patterns');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clientInvoices = invoices.filter(inv => inv.client_id === selectedClient);

  const getRiskColor = (level) => {
    const colors = {
      low: 'text-green-600 bg-green-50 border-green-200',
      medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      high: 'text-red-600 bg-red-50 border-red-200',
    };
    return colors[level] || 'text-slate-600 bg-slate-50 border-slate-200';
  };

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8" data-testid="ai-assistant-page">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="h-6 w-6 md:h-8 md:w-8 text-slate-900" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl md:text-4xl text-slate-900">AI Assistant</h1>
          </div>
          <p className="text-sm md:text-base text-slate-600">Generate collection emails and analyze payment patterns</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Email Generator */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="email-generator-card">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Generator
              </CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Generate personalized collection emails</p>
            </CardHeader>
            <CardContent className="p-4 md:p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-900">Select Invoice</label>
                <Select value={selectedInvoice} onValueChange={setSelectedInvoice}>
                  <SelectTrigger data-testid="email-invoice-select" className="rounded-sm">
                    <SelectValue placeholder="Choose an invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.map((invoice) => {
                      const client = clients.find(c => c.id === invoice.client_id);
                      return (
                        <SelectItem key={invoice.id} value={invoice.id}>
                          {invoice.invoice_number} - {client?.name} (${invoice.amount})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-900">Email Tone</label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="email-tone-select" className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="firm">Firm</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="generate-email-button"
                onClick={handleGenerateEmail}
                disabled={isGenerating || !selectedInvoice}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none btn-primary"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Email'
                )}
              </Button>

              {generatedEmail && (
                <div className="mt-6">
                  <label className="text-sm font-medium text-slate-900 mb-2 block">Generated Email</label>
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                    <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans" data-testid="generated-email-content">
                      {generatedEmail}
                    </pre>
                  </div>
                  <Button
                    data-testid="copy-email-button"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedEmail);
                      toast.success('Email copied to clipboard');
                    }}
                    className="w-full mt-3 rounded-none"
                  >
                    Copy to Clipboard
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Pattern Analysis */}
          <Card className="border border-slate-200 rounded-none shadow-none" data-testid="payment-analysis-card">
            <CardHeader className="border-b border-slate-100 p-4 md:p-6 bg-slate-50/50">
              <CardTitle className="font-heading text-lg md:text-xl flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Payment Pattern Analysis
              </CardTitle>
              <p className="text-xs md:text-sm text-slate-600 mt-1">Analyze client payment behavior</p>
            </CardHeader>
            <CardContent className="p-4 md:p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-900">Select Client</label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger data-testid="analysis-client-select" className="rounded-sm">
                    <SelectValue placeholder="Choose a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.company && `- ${client.company}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedClient && clientInvoices.length > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                  <p className="text-sm font-medium text-slate-900 mb-2">
                    Invoice History ({clientInvoices.length} invoices)
                  </p>
                  <div className="space-y-1 text-xs text-slate-600">
                    {clientInvoices.slice(0, 3).map((inv) => (
                      <div key={inv.id}>
                        {inv.invoice_number} - ${inv.amount} - {inv.status}
                      </div>
                    ))}
                    {clientInvoices.length > 3 && (
                      <div className="text-slate-500">+{clientInvoices.length - 3} more...</div>
                    )}
                  </div>
                </div>
              )}

              <Button
                data-testid="analyze-patterns-button"
                onClick={handleAnalyzePatterns}
                disabled={isAnalyzing || !selectedClient}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none btn-primary"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Analyze Patterns'
                )}
              </Button>

              {paymentAnalysis && (
                <div className="mt-6 space-y-4" data-testid="analysis-results">
                  <div>
                    <label className="text-sm font-medium text-slate-900 mb-2 block">Risk Level</label>
                    <div className={`px-4 py-2 border rounded-sm uppercase text-xs font-bold tracking-widest ${getRiskColor(paymentAnalysis.risk_level)}`}>
                      {paymentAnalysis.risk_level}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-900 mb-2 block">Analysis</label>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700">
                      {paymentAnalysis.analysis}
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-900 mb-2 block">Recommendations</label>
                    <ul className="space-y-2">
                      {paymentAnalysis.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="text-orange-500 mt-1">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AIAssistant;